import { useState, useRef, useCallback } from 'react';
import { AreaConfig } from '#server/models';
import { useAdminSubjectDraft } from '@/contexts';
import { useDraftFileUpload } from '../hooks/useDraftFileUpload';
import { useUndoRedo, useUndoRedoKeyboard } from '../hooks/useUndoRedo';
import { ADMIN_SCROLLBAR_CLASSES } from '../../constants';
import { AdminPreview } from '../components/AdminPreview';
import { DraftFileField } from '../components/DraftFileField';
import { FieldTooltip } from '../components/FieldTooltip';
import { PreviewPanel } from '../components/PreviewPanel';
import { UndoRedoButtons } from '../components/UndoRedoButtons';
import { Vec3Input } from '../components/Vec3Input';
import { NumberField } from '../components/NumberField';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

interface Step2Props {
  areaConfig: AreaConfig;
}

type TransformSnapshot = {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
};

export const Step2ThreeDConfig = ({ areaConfig }: Step2Props) => {
  const { draft, updateConfig } = useAdminSubjectDraft();
  const { config3D } = draft;
  const [showOriginAxes, setShowOriginAxes] = useState(true);
  const [transformMode, setTransformMode] = useState<'translate' | 'rotate' | 'scale' | null>(null);
  const { uploadFbx, uploadTexture } = useDraftFileUpload();
  const { push, undo, redo, canUndo, canRedo, reset } = useUndoRedo<TransformSnapshot>(50);

  // Reset history to current transform state when transform mode activates (null → non-null).
  const prevTransformModeRef = useRef(transformMode);
  if (transformMode !== null && prevTransformModeRef.current === null) {
    reset({ position: config3D.position, rotation: config3D.rotation, scale: config3D.scale });
  }
  prevTransformModeRef.current = transformMode;

  const applySnapshot = useCallback((snap: TransformSnapshot) => {
    updateConfig({ position: snap.position, rotation: snap.rotation, scale: snap.scale });
  }, [updateConfig]);

  useUndoRedoKeyboard(undo, redo, applySnapshot);

  const pushCurrent = useCallback(() => {
    push({ position: config3D.position, rotation: config3D.rotation, scale: config3D.scale });
  }, [push, config3D.position, config3D.rotation, config3D.scale]);

  if (draft.type !== '3D') {
    return (
      <div className="text-[#666] text-sm">
        3D Config is only available for {areaConfig.subjectName.plural.toLowerCase()} with type "3D".
      </div>
    );
  }

  return (
    <div className="flex gap-6 h-full">
      {/* Left: form fields */}
      <div className={`flex-1 space-y-4 overflow-y-auto pr-2 max-h-[calc(100vh-280px)] ${ADMIN_SCROLLBAR_CLASSES}`}>
        <h2 className="text-base font-semibold text-[#E8E8E8]">Step 2 – 3D Configuration</h2>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Model Files</h3>
          <DraftFileField
            label={`${areaConfig.subjectName.singular} FBX`}
            pathValue={config3D.modelPath}
            onPathChange={(v) => updateConfig({ modelPath: v })}
            blobUrl={config3D._modelBlobUrl}
            onFileChange={(f) => uploadFbx('_modelBlobUrl', f)}
            accept=".fbx"
            labelClassName="capitalize"
          />
          <DraftFileField
            label="Texture"
            pathValue={config3D.texturePath}
            onPathChange={(v) => updateConfig({ texturePath: v })}
            blobUrl={config3D._textureBlobUrl}
            onFileChange={uploadTexture}
            accept="image/*"
          />
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Transform</h3>
          <Vec3Input
            label="Scale"
            tooltip="Uniform scaling applied to the model on each axis. Use values less than 1 to shrink, greater than 1 to enlarge. Start with equal X/Y/Z values for proportional scaling."
            value={config3D.scale}
            onChange={(v) => updateConfig({ scale: v })}
            onCommit={pushCurrent}
            onAdjust={() => setTransformMode((prev) => (prev === 'scale' ? null : 'scale'))}
            isAdjusting={transformMode === 'scale'}
          />
          <Vec3Input
            label="Position"
            tooltip="Offset of the model from the scene origin (0, 0, 0) in world units. Adjust if the model is not centred in the viewer after loading."
            value={config3D.position}
            onChange={(v) => updateConfig({ position: v })}
            onCommit={pushCurrent}
            onAdjust={() => setTransformMode((prev) => (prev === 'translate' ? null : 'translate'))}
            isAdjusting={transformMode === 'translate'}
          />
          <Vec3Input
            label="Rotation (degrees)"
            tooltip="Initial rotation of the model in degrees around each axis (X = pitch, Y = yaw, Z = roll). Use this to correct the model's orientation if it was exported in a different coordinate system."
            value={config3D.rotation}
            onChange={(v) => updateConfig({ rotation: v })}
            onCommit={pushCurrent}
            onAdjust={() => setTransformMode((prev) => (prev === 'rotate' ? null : 'rotate'))}
            isAdjusting={transformMode === 'rotate'}
          />
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Camera</h3>
          <div className="grid grid-cols-3 gap-3">
            <NumberField
              label="Dist Min"
              tooltip="Closest the camera can zoom in to the model. Prevents the user from clipping inside the mesh."
              value={config3D.distMin}
              onChange={(v) => updateConfig({ distMin: v })}
            />
            <NumberField
              label="Dist Max"
              tooltip="Furthest the camera can zoom out from the model. Set this high enough that the full model (and any context models) remain visible."
              value={config3D.distMax}
              onChange={(v) => updateConfig({ distMax: v })}
            />
            <NumberField
              label="Dist (default)"
              tooltip="Starting camera distance when the viewer first loads. Should frame the model comfortably — typically somewhere between Dist Min and Dist Max."
              value={config3D.dist}
              onChange={(v) => updateConfig({ dist: v })}
            />
          </div>
        </section>

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Lighting</h3>
          <NumberField
            label="Ambient Light (0–3)"
            tooltip="Overall scene brightness. 1 is neutral; lower values create a darker, more dramatic look; higher values wash out shadows. Most models look best between 0.8 and 1.5."
            value={config3D.ambientLight}
            onChange={(v) => updateConfig({ ambientLight: v })}
          />
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-[#C3D82E] uppercase tracking-wide">Axes</h3>
            <FieldTooltip text="Displays directional axis labels (e.g. L/R, S/I, A/P) around the model to help orient the viewer. The labels rotate with the camera so they always point in the correct anatomical direction." />
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={config3D.axes} onCheckedChange={(v) => updateConfig({ axes: v })} />
            <Label className="text-[#999]">Show Axes</Label>
          </div>
          {config3D.axes && (
            <div className="grid grid-cols-3 gap-2">
              {(['x1', 'x2', 'y1', 'y2', 'z1', 'z2'] as const).map((key) => (
                <div key={key} className="space-y-1">
                  <Label className="text-[#777] text-xs">{key.toUpperCase()}</Label>
                  <Input
                    value={config3D.axesLabels[key]}
                    onChange={(e) => updateConfig({ axesLabels: { ...config3D.axesLabels, [key]: e.target.value } })}
                    className="bg-[#2A2A2A] border-[#444] text-[#E8E8E8] h-8 text-xs"
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Right: 3D Preview */}
      <PreviewPanel
        headerAction={
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-1.5 text-xs text-[#999] cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showOriginAxes}
                onChange={(e) => setShowOriginAxes(e.target.checked)}
                className="accent-[#C3D82E]"
              />
              Show origin axes
            </label>
            <div className="flex items-center gap-1">
              {(['translate', 'rotate', 'scale'] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setTransformMode((prev) => (prev === m ? null : m))}
                  className={
                    transformMode === m
                      ? 'px-2 py-0.5 text-xs rounded bg-[#C3D82E] text-[#1A1A1A] font-semibold'
                      : 'px-2 py-0.5 text-xs rounded text-[#888] hover:text-[#CCC] hover:bg-[#2A2A2A]'
                  }
                >
                  {m === 'translate' ? 'Move' : m === 'rotate' ? 'Rotate' : 'Scale'}
                </button>
              ))}
            </div>
          </div>
        }
        footer={
          <UndoRedoButtons
            canUndo={canUndo}
            onUndo={() => { const snap = undo(); if (snap) applySnapshot(snap); }}
            canRedo={canRedo}
            onRedo={() => { const snap = redo(); if (snap) applySnapshot(snap); }}
          />
        }
      >
        <AdminPreview
          draft={draft}
          showOriginAxes={showOriginAxes}
          transformMode={transformMode}
          onTransformChange={(t) => {
            updateConfig({ position: t.position, rotation: t.rotation, scale: t.scale });
            push({ position: t.position, rotation: t.rotation, scale: t.scale });
          }}
        />
      </PreviewPanel>
    </div>
  );
};
